import axios from 'axios';
import { API_URLS, API_CONFIG } from '../config/api';
import { authService } from './authService';

class PackagesService {
  constructor() {
    this.baseURL = API_URLS.PACKAGES;
    this.dateSlotsURL = API_URLS.DATE_SLOTS;
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      ...authService.getAuthHeaders(),
    };
  }

  // Packages CRUD operations
  async getPackages() {
    try {
      // Commented out API call for testing
      // const response = await axios.get(
      //   this.baseURL,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return [];
    } catch (error) {
      console.error('Error fetching packages:', error);
      throw error;
    }
  }

  async getPackage(id) {
    try {
      // Commented out API call for testing
      // const response = await axios.get(
      //   `${this.baseURL}/${id}`,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return null;
    } catch (error) {
      console.error('Error fetching package:', error);
      throw error;
    }
  }

  async createPackage(packageData) {
    try {
      // Commented out API call for testing
      // const response = await axios.post(
      //   this.baseURL,
      //   packageData,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return { id: 'mock-id', ...packageData };
    } catch (error) {
      console.error('Error creating package:', error);
      throw error;
    }
  }

  async updatePackage(id, packageData) {
    try {
      // Commented out API call for testing
      // const response = await axios.put(
      //   `${this.baseURL}/${id}`,
      //   packageData,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return { id, ...packageData };
    } catch (error) {
      console.error('Error updating package:', error);
      throw error;
    }
  }

  async deletePackage(id) {
    try {
      // Commented out API call for testing
      // await axios.delete(`${this.baseURL}/${id}`, {
      //   headers: this.getHeaders()
      // });
      
      // Mock success for testing
      return true;
    } catch (error) {
      console.error('Error deleting package:', error);
      throw error;
    }
  }

  // Date Slots operations
  async getDateSlots(packageId) {
    try {
      // Commented out API call for testing
      // const response = await axios.get(
      //   `${this.dateSlotsURL}?packageId=${packageId}`,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return [];
    } catch (error) {
      console.error('Error fetching date slots:', error);
      throw error;
    }
  }

  async createDateSlot(slotData) {
    try {
      // Commented out API call for testing
      // const response = await axios.post(
      //   this.dateSlotsURL,
      //   slotData,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return { id: 'mock-slot-id', ...slotData };
    } catch (error) {
      console.error('Error creating date slot:', error);
      throw error;
    }
  }

  async updateDateSlot(id, slotData) {
    try {
      // Commented out API call for testing
      // const response = await axios.put(
      //   `${this.dateSlotsURL}/${id}`,
      //   slotData,
      //   { headers: this.getHeaders() }
      // );
      // return response.data.data;
      
      // Mock data for testing
      return { id, ...slotData };
    } catch (error) {
      console.error('Error updating date slot:', error);
      throw error;
    }
  }

  async deleteDateSlot(id) {
    try {
      // Commented out API call for testing
      // await axios.delete(`${this.dateSlotsURL}/${id}`, {
      //   headers: this.getHeaders()
      // });
      
      // Mock success for testing
      return true;
    } catch (error) {
      console.error('Error deleting date slot:', error);
      throw error;
    }
  }

  // Holiday Packages operations
  async getHolidayPackages(filters = {}) {
    try {
      const { destination, category, search, page, limit } = filters;
      const params = new URLSearchParams();
      
      if (destination) params.append('destination', destination);
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      
      // Add cache buster
      const cacheBuster = `cb=${Date.now()}`;
      params.append('cb', Date.now());
      
      const queryString = params.toString();
      const url = `${API_URLS.HOLIDAY_PACKAGES}?${queryString}`;
      
      const response = await axios.get(url, {
        headers: {
          ...this.getHeaders(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0'
        }
      });
      
      // Handle the API response structure - expecting pagination data similar to AllBookings
      return {
        success: true,
        data: response.data.data || response.data || [],
        pagination: response.data.pagination || null,
        message: 'Packages fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching holiday packages:', error);
      // If backend signals expired/invalid token, dispatch a global event
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  async createHolidayPackage(packageData) {
    try {
      const response = await axios.post(API_URLS.HOLIDAY_PACKAGES, packageData, {
        headers: this.getHeaders()
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Package created successfully'
      };
    } catch (error) {
      console.error('Error creating holiday package:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  async deleteHolidayPackage(packageId) {
    try {
      const response = await axios.delete(`${API_URLS.HOLIDAY_PACKAGES}/${packageId}`, {
        headers: this.getHeaders()
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Package deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting holiday package:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  async getHolidayPackage(packageId) {
    try {
      const response = await axios.get(`${API_URLS.HOLIDAY_PACKAGES}/${packageId}`, {
        headers: this.getHeaders()
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Package fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching holiday package:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  async updateHolidayPackage(packageId, packageData) {
    try {
      const response = await axios.put(`${API_URLS.HOLIDAY_PACKAGES}/${packageId}`, packageData, {
        headers: this.getHeaders()
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Package updated successfully'
      };
    } catch (error) {
      console.error('Error updating holiday package:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  // Date Slots Management
  async updateDateSlots(packageId, slotData) {
    try {
      const response = await axios.put(`${API_URLS.HOLIDAY_PACKAGES}/slot/${packageId}`, slotData, {
        headers: this.getHeaders()
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Date slots updated successfully'
      };
    } catch (error) {
      console.error('Error updating date slots:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  async deleteDateSlot(packageId, dates) {
    try {
      const response = await axios.delete(`${API_URLS.HOLIDAY_PACKAGES}/slot/${packageId}`, {
        headers: this.getHeaders(),
        data: { date: dates }
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Date slot deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting date slot:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  // Package Status Update
  async updatePackageStatus(packageId, statusData) {
    try {
      const response = await axios.put(`${API_URLS.HOLIDAY_PACKAGES}/status/${packageId}`, statusData, {
        headers: this.getHeaders()
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Package status updated successfully'
      };
    } catch (error) {
      console.error('Error updating package status:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  // AWS Image Upload
  async getAwsImageLinks(payload) {
    try {
      // Check if user is authenticated before making the request
      if (!authService.isAuthenticated()) {
        throw new Error('User not authenticated. Please login again.');
      }

      console.log('Getting AWS upload links with payload:', payload);
      console.log('Headers:', this.getHeaders());
      console.log('Request URL:', `${API_CONFIG.BASE_URL}/vendor/uploadURL`);
      
      const response = await axios.get(`${API_CONFIG.BASE_URL}/vendor/uploadURL`, {
        params: payload,
        headers: this.getHeaders(),
        timeout: 30000 // 30 second timeout
      });
      
      console.log('AWS upload links response:', response.data);
      
      if (!response.data) {
        throw new Error('Empty response from server');
      }
      
      if (!response.data.uploadUrl) {
        console.error('Missing uploadUrl in response:', response.data);
        throw new Error('Invalid response: missing upload URL');
      }
      
      if (!response.data.viewUrl) {
        console.error('Missing viewUrl in response:', response.data);
        throw new Error('Invalid response: missing view URL');
      }
      
      // Log the actual pre-signed URL to debug the AWS region issue
      console.log('Pre-signed URL:', response.data.uploadUrl);
      console.log('URL contains region:', response.data.uploadUrl.includes('region=') || response.data.uploadUrl.includes('X-Amz-Credential'));
      
      // Check if the URL is properly encoded
      try {
        const url = new URL(response.data.uploadUrl);
        console.log('Parsed URL components:', {
          hostname: url.hostname,
          pathname: url.pathname,
          search: url.search
        });
        
        // Check for region in query parameters
        const regionParam = url.searchParams.get('X-Amz-Credential');
        console.log('X-Amz-Credential parameter:', regionParam);
        if (regionParam) {
          const parts = regionParam.split('/');
          console.log('Credential parts:', parts);
          if (parts.length >= 4) {
            console.log('Region from credential:', parts[3]);
          }
        }
      } catch (e) {
        console.error('Error parsing URL:', e);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error getting AWS upload links:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // If token expired, throw a specific error instead of redirecting
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        throw new Error('Your session has expired. Please refresh the page and login again.');
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
      }
      
      if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      }
      
      throw error;
    }
  }

  async uploadAwsImage(url, file) {
    try {
      console.log('Uploading to AWS S3:', url);
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Try using native fetch first (like Angular's HttpClient)
      console.log('Uploading file directly to AWS S3 using native fetch...');
      
      let response;
      try {
        // Use fetch like Angular's HttpClient
        const fetchResponse = await fetch(url, {
          method: 'PUT',
          body: file,
          // Don't set any headers - let AWS S3 handle content type from the pre-signed URL
        });
        
        if (!fetchResponse.ok) {
          const errorText = await fetchResponse.text();
          throw new Error(`Upload failed with status ${fetchResponse.status}: ${errorText}`);
        }
        
        response = { status: fetchResponse.status, data: {} };
        console.log('AWS S3 upload successful with fetch');
      } catch (fetchError) {
        console.log('Fetch upload failed, trying with fresh axios instance...');
        
        // Fallback to fresh axios instance
        const axiosInstance = axios.create();
        response = await axiosInstance.put(url, file, {
          timeout: 60000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          // Don't set any headers - let AWS S3 handle content type from the pre-signed URL
          transformRequest: [(data) => data] // Ensure data is sent as-is
        });
      }
      
      console.log('AWS S3 upload response:', response.status);
      return response.data;
    } catch (error) {
      console.error('Error uploading image to AWS:', error);
      console.error('Upload error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // Log the full error response for debugging
      if (error.response?.data) {
        console.error('Full error response:', error.response.data);
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timeout. Please try again.');
      }
      
      if (error.response?.status === 403) {
        throw new Error('Access denied. The upload URL may have expired.');
      }
      
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        console.error('400 Error details:', errorData);
        
        if (errorData && typeof errorData === 'string') {
          if (errorData.includes('InvalidRequest')) {
            throw new Error('Invalid file format. Please ensure the file is a valid image (JPG, PNG, GIF, or WebP).');
          } else if (errorData.includes('SignatureDoesNotMatch')) {
            throw new Error('Upload URL signature is invalid. Please try again.');
          } else if (errorData.includes('TokenRefreshRequired')) {
            throw new Error('Your session has expired. Please refresh the page and login again.');
          } else if (errorData.includes('AuthorizationQueryParametersError')) {
            throw new Error('AWS configuration error. Please contact support or try again later.');
          } else if (errorData.includes('X-Amz-Credential')) {
            throw new Error('AWS credentials error. Please contact support.');
          }
        }
        throw new Error('Invalid request. Please check the file format and try again.');
      }
      
      throw error;
    }
  }

  // Dashboard API
  async getDashboardData() {
    try {
      const cacheBuster = `cb=${Date.now()}`;
      const url = API_URLS.DASHBOARD.includes('?')
        ? `${API_URLS.DASHBOARD}&${cacheBuster}`
        : `${API_URLS.DASHBOARD}?${cacheBuster}`;

      const response = await axios.get(url, {
        headers: {
          ...this.getHeaders(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0'
        }
      });
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: 'Dashboard data fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // If backend signals expired/invalid token, dispatch a global event
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

  // Bookings API
  async getAllBookings(params = {}) {
    try {
      const cacheBuster = `cb=${Date.now()}`;
      const searchParams = new URLSearchParams(params);
      searchParams.append('' + 'cb', Date.now());
      const url = `${API_URLS.BOOKINGS}?${searchParams.toString()}`;

      const response = await axios.get(url, {
        headers: {
          ...this.getHeaders(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0'
        }
      });

      return {
        success: true,
        data: response.data.data || response.data || [],
        pagination: response.data.pagination || undefined,
        filters: response.data.filters || undefined,
        message: 'Bookings fetched successfully'
      };
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (error.response?.status === 410 || error.response?.data?.msg === 'Access Token Expired') {
        const event = new Event('session-expired');
        window.dispatchEvent(event);
      }
      throw error;
    }
  }

}

export const packagesService = new PackagesService();
